import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Download, Search, FileText, LayoutList, Grid3x3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { moyenneNormalisee } from '../utils/normalizeNote';
import { PodiumStagiaires } from '../components/carnet-general/PodiumStagiaires';
import { SyntheseTable } from '../components/carnet-general/SyntheseTable';
import { GrilleDetaillee } from '../components/carnet-general/GrilleDetaillee';
import { DetailNotesDialog } from '../components/carnet-general/DetailNotesDialog';

const calcMoyenne = (row) => moyenneNormalisee(row.liste);

const VUES = [
  { id: 'synthese', label: 'Synthèse', icon: LayoutList },
  { id: 'grille', label: 'Grille détaillée', icon: Grid3x3 },
];

const CarnetGeneral = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('nom');
  const [sortDir, setSortDir] = useState('asc');
  const [vue, setVue] = useState('synthese');
  const [selection, setSelection] = useState(null);

  useEffect(() => { loadNotes(); }, []);

  const loadNotes = async () => {
    try {
      const res = await axios.get('/notes/all-formateurs');
      // Notes orphelines (stagiaire supprimé) écartées du carnet
      setNotes(res.data.filter(n => n.stagiaire_nom !== 'Inconnu'));
    } catch { toast.error('Erreur chargement notes'); }
    finally { setLoading(false); }
  };

  // Lignes par stagiaire + colonnes catégorie/item + catégories
  const { rows, columns, categories } = useMemo(() => {
    const comboSet = new Map();
    const stagMap = new Map();

    for (const raw of notes) {
      const sid = raw.stagiaire_id;
      if (!sid) continue;
      const n = { ...raw, categorie: raw.categorie || 'Autre', item: raw.item || raw.matiere || '—' };
      const key = `${n.categorie}|${n.item}`;
      if (!comboSet.has(key)) comboSet.set(key, { categorie: n.categorie, item: n.item });

      if (!stagMap.has(sid)) {
        stagMap.set(sid, { stagiaire_id: sid, nom: n.stagiaire_nom || '?', notes: {}, liste: [] });
      }
      const entry = stagMap.get(sid);
      if (!entry.notes[key]) entry.notes[key] = [];
      entry.notes[key].push(n);
      entry.liste.push(n);
    }

    const cols = [...comboSet.entries()]
      .sort((a, b) => a[1].categorie.localeCompare(b[1].categorie) || a[1].item.localeCompare(b[1].item))
      .map(([key, val]) => ({ key, ...val }));
    const cats = [...new Set(cols.map(c => c.categorie))];
    return { rows: [...stagMap.values()], columns: cols, categories: cats };
  }, [notes]);

  // NIM / prénom / nom depuis les fiches stagiaires
  const [usersMap, setUsersMap] = useState({});
  useEffect(() => {
    if (rows.length === 0) return;
    axios.get('/stagiaires').then(res => {
      const map = {};
      for (const s of res.data) {
        const ui = s.user_info || s;
        const uid = s.user_id || ui.user_id;
        if (uid) {
          map[uid] = {
            numero_candidat: ui.numero_candidat || s.numero_candidat || '',
            prenom: ui.prenom || s.prenom || '',
            nom: ui.nom || s.nom || ''
          };
        }
      }
      setUsersMap(map);
    }).catch(() => {});
  }, [rows.length]);

  const filteredRows = useMemo(() => {
    let filtered = rows.map(r => {
      const info = usersMap[r.stagiaire_id] || {};
      return { ...r, nim: info.numero_candidat || '', prenom: info.prenom || r.nom.split(' ')[0] || '', nomFamille: info.nom || r.nom.split(' ').slice(1).join(' ') || '' };
    });
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(r =>
        r.nim.toString().toLowerCase().includes(s) ||
        r.prenom.toLowerCase().includes(s) ||
        r.nomFamille.toLowerCase().includes(s)
      );
    }
    filtered.sort((a, b) => {
      let va, vb;
      if (sortCol === 'nim') { va = a.nim; vb = b.nim; }
      else if (sortCol === 'moyenne') { va = calcMoyenne(a); vb = calcMoyenne(b); }
      else { va = `${a.nomFamille} ${a.prenom}`; vb = `${b.nomFamille} ${b.prenom}`; }
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return filtered;
  }, [rows, usersMap, search, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'moyenne' ? 'desc' : 'asc'); }
  };

  const exportCSV = () => {
    const header = ['NIM', 'Prenom', 'Nom', ...columns.map(c => `${c.categorie} / ${c.item}`), 'Moyenne /10'];
    const csvRows = [header.join(';')];
    for (const r of filteredRows) {
      const row = [
        r.nim,
        r.prenom,
        r.nomFamille,
        ...columns.map(c => (r.notes[c.key] || []).map(n => n.note).join(' | ') || '-'),
        calcMoyenne(r).toFixed(1)
      ];
      csvRows.push(row.join(';'));
    }
    const blob = new Blob(['\ufeff' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `carnet_general_notes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  const exportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
    const today = new Date().toLocaleDateString('fr-FR');

    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    doc.text('Carnet General des Notes', 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`${filteredRows.length} stagiaires - ${notes.length} notes - ${today}`, 14, 21);

    const head = [['NIM', 'Stagiaire', ...columns.map(c => `${c.categorie}\n${c.item}`), 'Moy. /10']];
    const body = filteredRows.map(r => {
      const moy = calcMoyenne(r);
      return [
        r.nim || '-',
        `${r.prenom} ${r.nomFamille}`,
        ...columns.map(c => {
          const cellNotes = r.notes[c.key] || [];
          return cellNotes.length > 0 ? cellNotes.map(n => n.note).join(' / ') : '-';
        }),
        moy > 0 ? moy.toFixed(1) : '-'
      ];
    });

    autoTable(doc, {
      head,
      body,
      startY: 25,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak', halign: 'center' },
      headStyles: { fillColor: [141, 198, 63], textColor: 255, fontSize: 6, fontStyle: 'bold', halign: 'center', valign: 'middle' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'left', cellWidth: 35 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index >= 2) {
          const val = parseFloat(data.cell.raw);
          if (!isNaN(val)) {
            if (val <= 4) { data.cell.styles.textColor = [185, 28, 28]; data.cell.styles.fillColor = [254, 226, 226]; }
            else if (val <= 7) { data.cell.styles.textColor = [154, 52, 18]; data.cell.styles.fillColor = [255, 237, 213]; }
            else { data.cell.styles.textColor = [22, 101, 52]; data.cell.styles.fillColor = [220, 252, 231]; }
          }
          if (data.column.index === columns.length + 2) {
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: 10, right: 10 }
    });

    doc.save(`carnet_general_notes_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF téléchargé');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-[#8DC63F] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="carnet-general-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-1">Carnet Général</h1>
          <p className="text-slate-600">{notes.length} notes — {filteredRows.length} stagiaires — moyennes sur 10</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportPDF} className="bg-[#0F172A] hover:bg-[#1e293b] text-white" size="sm" data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button onClick={exportCSV} className="bg-[#8DC63F] hover:bg-[#7db336] text-white" size="sm" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <PodiumStagiaires />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par NIM, prénom ou nom..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="search-carnet"
          />
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" role="tablist">
          {VUES.map(v => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVue(v.id)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${vue === v.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid={`carnet-vue-${v.id}`}
            >
              <v.icon className="h-4 w-4" />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {columns.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-slate-300 p-8 text-center">
          <BookOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Aucune note enregistrée</p>
        </div>
      ) : vue === 'synthese' ? (
        <>
          <SyntheseTable rows={filteredRows} categories={categories} onSelect={setSelection} onSort={handleSort} sortCol={sortCol} />
          <p className="text-xs text-slate-500">Cliquez sur un stagiaire pour voir toutes ses notes par support et par catégorie. Vert ≥ 8, orange de 5 à moins de 8, rouge &lt; 5.</p>
        </>
      ) : (
        <GrilleDetaillee rows={filteredRows} columns={columns} calcMoyenne={calcMoyenne} onSort={handleSort} sortCol={sortCol} />
      )}

      <DetailNotesDialog stagiaire={selection} onClose={() => setSelection(null)} />
    </div>
  );
};

export default CarnetGeneral;
